import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { useNodeLifecycleCounts } from '@/hooks/useMetrics';
import { LIFECYCLE_STATES, COMMISSIONED_PERIOD_OPTIONS } from '@/utils/constants';
import type { LifecycleState } from '@/types/node';

const TILE_TOOLTIPS: Partial<Record<LifecycleState, string>> = {
  new: 'Provisioned in the portal but awaiting physical install.',
};

interface NodeLifecycleBarProps {
  activeFilter: LifecycleState | null;
  onFilterChange: (filter: LifecycleState | null) => void;
  commissionedDays: number;
  onCommissionedDaysChange: (days: number) => void;
}

export function NodeLifecycleBar({
  activeFilter,
  onFilterChange,
  commissionedDays,
  onCommissionedDaysChange,
}: NodeLifecycleBarProps) {
  const { counts, loading } = useNodeLifecycleCounts(commissionedDays);
  const [customDays, setCustomDays] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  if (loading || !counts) return null;

  const total = counts.online + counts.offline + counts.new + counts.recentlyCommissioned;

  const tiles: { key: LifecycleState; count: number }[] = [
    { key: 'online', count: counts.online },
    { key: 'offline', count: counts.offline },
    { key: 'new', count: counts.new },
    { key: 'recentlyCommissioned', count: counts.recentlyCommissioned },
  ];

  const handlePeriodChange = (value: string) => {
    if (value === 'custom') {
      setShowCustom(true);
      return;
    }
    setShowCustom(false);
    onCommissionedDaysChange(Number(value));
  };

  const handleCustomSubmit = () => {
    const days = parseInt(customDays, 10);
    if (days > 0) {
      onCommissionedDaysChange(days);
      setShowCustom(false);
    }
  };

  return (
    <div className="dls-flex dls-items-end dls-gap-3">
      <div className="dls-flex dls-gap-2 dls-flex-1">
        {tiles.map((tile) => {
          const config = LIFECYCLE_STATES[tile.key];
          const pct = total > 0 ? ((tile.count / total) * 100).toFixed(1) : '0';
          const isActive = activeFilter === tile.key;
          const isDimmed = activeFilter !== null && !isActive;

          const tooltip = TILE_TOOLTIPS[tile.key];
          const btn = (
            <button
              key={tile.key}
              type="button"
              onClick={() => onFilterChange(isActive ? null : tile.key)}
              className={`dls-flex-1 dls-rounded-lg dls-px-3 dls-py-1.5 dls-transition-all dls-cursor-pointer ${config.color} ${
                isActive ? 'dls-ring-2 dls-ring-offset-1 dls-ring-current/30' : ''
              } hover:dls-ring-2 hover:dls-ring-offset-1 hover:dls-ring-current/20 ${
                isDimmed ? 'dls-opacity-40' : ''
              }`}
            >
              <div className="dls-flex dls-items-center dls-justify-center dls-gap-2">
                <span className={`dls-text-lg dls-font-bold ${config.textColor}`}>{tile.count}</span>
                <span className={`dls-text-sm dls-font-bold ${config.textColor} dls-opacity-80`}>
                  {config.label}
                </span>
                <span className={`dls-text-sm dls-font-bold ${config.textColor} dls-opacity-60`}>{pct}%</span>
              </div>
            </button>
          );

          if (tooltip) {
            return (
              <TooltipProvider key={tile.key} delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>{btn}</TooltipTrigger>
                  <TooltipContent className="dls-max-w-xs dls-text-left">
                    <p>{tooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }

          return btn;
        })}
      </div>

      {/* Period picker */}
      <div className="dls-flex dls-flex-col dls-items-end dls-gap-1 dls-shrink-0">
        <span className="dls-text-xs dls-text-muted-foreground">Commission period</span>
        {showCustom ? (
          <div className="dls-flex dls-items-center dls-gap-1">
            <Input
              type="number"
              min={1}
              placeholder="Days"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
              className="dls-w-20 dls-h-8 dls-text-sm"
              autoFocus
            />
            <button
              type="button"
              onClick={handleCustomSubmit}
              className="dls-text-xs dls-text-primary hover:dls-underline dls-px-1"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => setShowCustom(false)}
              className="dls-text-xs dls-text-muted-foreground hover:dls-underline dls-px-1"
            >
              Cancel
            </button>
          </div>
        ) : (
          <Select
            value={
              COMMISSIONED_PERIOD_OPTIONS.some((o) => o.value === commissionedDays)
                ? String(commissionedDays)
                : 'custom'
            }
            onValueChange={handlePeriodChange}
          >
            <SelectTrigger className="dls-w-[120px] dls-h-8 dls-text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMMISSIONED_PERIOD_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))}
              <SelectItem value="custom">Custom...</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}
